import { Box3, Matrix3, Matrix4, Quaternion, Vector3 } from 'three';
/**
 * Generate model matrix (world space) for a cuboid so we can calculate each
 * fragment location in the cuboid's local space.
 * Min and max values are also calculated for each cuboid to determine
 * if the fragment is inside the cuboid.
 *
 * Ported from the Editor's `Points` service — shared because both the mesh masking texture and the
 * mask-gated point picking build on it.
 *
 * @param cuboid Cuboid to generate data
 * @param opacity Opacity value
 */
export const generateCuboidUniforms = (cuboid, opacity = 1) => {
    const { center, rotation, extent } = cuboid;
    const boundingBox = new Box3(new Vector3(-extent[0] / 2, -extent[1] / 2, -extent[2] / 2), new Vector3(extent[0] / 2, extent[1] / 2, extent[2] / 2));
    // Calculate the model matrix
    const modelMatrix = new Matrix4();
    modelMatrix
        .compose(new Vector3(...center), new Quaternion().setFromRotationMatrix(new Matrix4().setFromMatrix3(new Matrix3().fromArray(rotation))), new Vector3(1, 1, 1))
        .invert();
    return {
        modelMatrix,
        min: boundingBox.min,
        max: boundingBox.max,
        opacity,
    };
};
/**
 * Get the bounding box of a cylinder
 *
 * @param transform
 * @param diameter
 * @param length
 * @returns
 */
export const getCylinderBoundingBox = (cylinder) => {
    if (cylinder.bbox) {
        return new Box3(new Vector3().fromArray(cylinder.bbox[0]), new Vector3().fromArray(cylinder.bbox[1]));
    }
    const { diameter, length, transformation } = cylinder;
    const matrix = new Matrix4().set(...transformation);
    const offsetMatrix = new Matrix4().makeTranslation(0, 0, length / 2);
    const radius = diameter / 2;
    // Generate bounding box corners in local space
    const localCorners = [
        new Vector3(-radius, -radius, -length / 2), // Min
        new Vector3(radius, -radius, -length / 2), // Bottom-back-right
        new Vector3(-radius, radius, -length / 2), // Bottom-front-left
        new Vector3(radius, radius, -length / 2), // Bottom-front-right
        new Vector3(-radius, -radius, length / 2), // Top-back-left
        new Vector3(radius, -radius, length / 2), // Top-back-right
        new Vector3(-radius, radius, length / 2), // Top-front-left
        new Vector3(radius, radius, length / 2), // Max
    ];
    // Transform corners to world space and create bounding box
    const boundingBox = new Box3().setFromPoints(localCorners.map((corner) => corner.applyMatrix4(offsetMatrix).applyMatrix4(matrix)));
    return boundingBox;
};
